import React, { useState } from 'react';
import {
  Box,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Select,
  HStack,
  VStack,
  Alert,
  AlertIcon,
  Spinner,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Badge,
  Checkbox,
  useToast,
  Flex,
  Divider
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon, AddIcon } from '@chakra-ui/icons';
import useProjectAccess from '../../hooks/admin/useProjectAccess';

const ProjectAccess = () => {
  const {
    users,
    projects,
    loading,
    error,
    fetchUserProjects,
    fetchProjectUsers,
    grantAccess,
    revokeAccess
  } = useProjectAccess();

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [userProjects, setUserProjects] = useState([]);
  const [projectUsers, setProjectUsers] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [accessMode, setAccessMode] = useState('user'); // 'user' or 'project'
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [projectsToAdd, setProjectsToAdd] = useState([]);
  const [usersToAdd, setUsersToAdd] = useState([]);
  const toast = useToast();

  // ユーザーが選択された時の処理
  const handleUserSelect = async (event) => {
    const userId = event.target.value;
    if (!userId) {
      setSelectedUser(null);
      setUserProjects([]);
      return;
    }

    setSelectedUser(userId);
    setLoadingDetails(true);
    
    try {
      const projects = await fetchUserProjects(userId);
      setUserProjects(projects);
    } catch (error) {
      console.error('ユーザープロジェクト取得エラー:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  // プロジェクトが選択された時の処理
  const handleProjectSelect = async (event) => {
    const projectId = event.target.value;
    if (!projectId) {
      setSelectedProject(null);
      setProjectUsers([]);
      return;
    }

    setSelectedProject(projectId);
    setLoadingDetails(true);
    
    try {
      const users = await fetchProjectUsers(projectId);
      setProjectUsers(users);
    } catch (error) {
      console.error('プロジェクトユーザー取得エラー:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  // アクセス権の付与モーダルを開く
  const handleOpenAddAccess = (mode) => {
    setAccessMode(mode);
    setProjectsToAdd([]);
    setUsersToAdd([]);
    onOpen();
  };

  // アクセス権を付与する
  const handleGrantAccess = async () => {
    if (accessMode === 'user' && selectedUser && projectsToAdd.length > 0) {
      for (const projectId of projectsToAdd) {
        await grantAccess(selectedUser, projectId);
      }
      // 最新のユーザープロジェクトを取得
      const updatedProjects = await fetchUserProjects(selectedUser);
      setUserProjects(updatedProjects);
    } else if (accessMode === 'project' && selectedProject && usersToAdd.length > 0) {
      for (const userId of usersToAdd) {
        await grantAccess(userId, selectedProject);
      }
      // 最新のプロジェクトユーザーを取得
      const updatedUsers = await fetchProjectUsers(selectedProject);
      setProjectUsers(updatedUsers);
    }
    
    onClose();
  };

  // アクセス権を削除する
  const handleRevokeAccess = async (userId, projectId) => {
    if (!window.confirm('このアクセス権を削除してもよろしいですか？')) {
      return;
    }
    
    const success = await revokeAccess(userId, projectId);
    
    if (success) {
      if (accessMode === 'user') {
        // ユーザーのプロジェクト一覧から削除
        setUserProjects(prev => prev.filter(p => p.id !== projectId));
      } else {
        // プロジェクトのユーザー一覧から削除
        setProjectUsers(prev => prev.filter(u => u.email !== userId));
      }
    }
  };

  // プロジェクト追加のチェックボックス変更
  const handleProjectCheckboxChange = (projectId) => {
    if (projectsToAdd.includes(projectId)) {
      setProjectsToAdd(projectsToAdd.filter(id => id !== projectId));
    } else {
      setProjectsToAdd([...projectsToAdd, projectId]);
    }
  };

  // ユーザー追加のチェックボックス変更
  const handleUserCheckboxChange = (userId) => {
    if (usersToAdd.includes(userId)) {
      setUsersToAdd(usersToAdd.filter(id => id !== userId));
    } else {
      setUsersToAdd([...usersToAdd, userId]);
    }
  };

  if (loading) return <Spinner />;
  if (error) return <Alert status="error"><AlertIcon />{error}</Alert>;

  return (
    <Box>
      <Tabs variant="enclosed" colorScheme="blue">
        <TabList>
          <Tab>ユーザー別アクセス管理</Tab>
          <Tab>プロジェクト別アクセス管理</Tab>
        </TabList>

        <TabPanels>
          {/* ユーザー別アクセス管理 */}
          <TabPanel>
            <VStack align="stretch" spacing={4}>
              <Box>
                <Text mb={2} fontWeight="bold">ユーザーを選択</Text>
                <HStack>
                  <Select 
                    placeholder="ユーザーを選択" 
                    onChange={handleUserSelect}
                    value={selectedUser || ''}
                  >
                    {users.map(user => (
                      <option key={user.email} value={user.email}>
                        {user.email} ({user.role})
                      </option>
                    ))}
                  </Select>
                  <Button 
                    colorScheme="blue" 
                    leftIcon={<AddIcon />}
                    onClick={() => handleOpenAddAccess('user')}
                    isDisabled={!selectedUser}
                  >
                    アクセス権を追加
                  </Button>
                </HStack>
              </Box>

              {loadingDetails ? (
                <Spinner />
              ) : selectedUser ? (
                <Box>
                  <Text fontWeight="bold" mb={2}>アクセス可能なプロジェクト</Text>
                  {userProjects.length === 0 ? (
                    <Alert status="info">
                      <AlertIcon />
                      このユーザーにはアクセス可能なプロジェクトがありません。
                    </Alert>
                  ) : (
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>プロジェクト名</Th>
                          <Th>作成日</Th>
                          <Th width="100px">アクション</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {userProjects.map(project => (
                          <Tr key={project.id}>
                            <Td>{project.name}</Td>
                            <Td>{new Date(project.created_at).toLocaleDateString()}</Td>
                            <Td>
                              <Button
                                size="xs"
                                colorScheme="red"
                                leftIcon={<CloseIcon />}
                                onClick={() => handleRevokeAccess(selectedUser, project.id)}
                              >
                                削除
                              </Button>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  )}
                </Box>
              ) : (
                <Alert status="info">
                  <AlertIcon />
                  ユーザーを選択してください。
                </Alert>
              )}
            </VStack>
          </TabPanel>

          {/* プロジェクト別アクセス管理 */}
          <TabPanel>
            <VStack align="stretch" spacing={4}>
              <Box>
                <Text mb={2} fontWeight="bold">プロジェクトを選択</Text>
                <HStack>
                  <Select 
                    placeholder="プロジェクトを選択" 
                    onChange={handleProjectSelect}
                    value={selectedProject || ''}
                  >
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </Select>
                  <Button 
                    colorScheme="blue" 
                    leftIcon={<AddIcon />}
                    onClick={() => handleOpenAddAccess('project')}
                    isDisabled={!selectedProject}
                  >
                    ユーザーを追加
                  </Button>
                </HStack>
              </Box>

              {loadingDetails ? (
                <Spinner />
              ) : selectedProject ? (
                <Box>
                  <Text fontWeight="bold" mb={2}>アクセス権を持つユーザー</Text>
                  {projectUsers.length === 0 ? (
                    <Alert status="info">
                      <AlertIcon />
                      このプロジェクトにアクセスできるユーザーはいません。
                    </Alert>
                  ) : (
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>メールアドレス</Th>
                          <Th>権限</Th>
                          <Th width="100px">アクション</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {projectUsers.map(user => (
                          <Tr key={user.email}>
                            <Td>{user.email}</Td>
                            <Td>
                              <Badge colorScheme={user.role === 'admin' ? 'red' : 'blue'}>
                                {user.role}
                              </Badge>
                            </Td>
                            <Td>
                              <Button
                                size="xs"
                                colorScheme="red"
                                leftIcon={<CloseIcon />}
                                onClick={() => handleRevokeAccess(user.email, selectedProject)}
                              >
                                削除
                              </Button>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  )}
                </Box>
              ) : (
                <Alert status="info">
                  <AlertIcon />
                  プロジェクトを選択してください。
                </Alert>
              )}
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* アクセス権追加モーダル */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {accessMode === 'user' 
              ? `ユーザー「${selectedUser}」にプロジェクトを追加` 
              : `プロジェクト「${projects.find(p => p.id === selectedProject)?.name}」にユーザーを追加`}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {accessMode === 'user' ? (
              // ユーザーにプロジェクトを追加
              <Box>
                <Text mb={3}>追加するプロジェクトを選択してください：</Text>
                <VStack align="stretch" maxH="300px" overflowY="auto">
                  {projects
                    .filter(project => !userProjects.some(p => p.id === project.id))
                    .map(project => (
                      <Flex key={project.id} justify="space-between" align="center" py={1}>
                        <Box>
                          <Checkbox 
                            isChecked={projectsToAdd.includes(project.id)}
                            onChange={() => handleProjectCheckboxChange(project.id)}
                          >
                            {project.name}
                          </Checkbox>
                          <Text fontSize="xs" color="gray.600">
                            作成日: {new Date(project.created_at).toLocaleDateString()}
                          </Text>
                        </Box>
                      </Flex>
                    ))}
                  {projects.filter(project => !userProjects.some(p => p.id === project.id)).length === 0 && (
                    <Text color="gray.500">追加できるプロジェクトがありません</Text>
                  )}
                </VStack>
              </Box>
            ) : (
              // プロジェクトにユーザーを追加
              <Box>
                <Text mb={3}>追加するユーザーを選択してください：</Text>
                <VStack align="stretch" maxH="300px" overflowY="auto">
                  {users
                    .filter(user => !projectUsers.some(u => u.email === user.email))
                    .map(user => (
                      <Flex key={user.email} justify="space-between" align="center" py={1}>
                        <Box>
                          <Checkbox 
                            isChecked={usersToAdd.includes(user.email)}
                            onChange={() => handleUserCheckboxChange(user.email)}
                          >
                            {user.email}
                          </Checkbox>
                          <Text fontSize="xs" color="gray.600">
                            権限: {user.role}
                          </Text>
                        </Box>
                      </Flex>
                    ))}
                  {users.filter(user => !projectUsers.some(u => u.email === user.email)).length === 0 && (
                    <Text color="gray.500">追加できるユーザーがありません</Text>
                  )}
                </VStack>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              キャンセル
            </Button>
            <Button 
              colorScheme="blue" 
              leftIcon={<CheckIcon />}
              onClick={handleGrantAccess}
              isDisabled={
                (accessMode === 'user' && projectsToAdd.length === 0) || 
                (accessMode === 'project' && usersToAdd.length === 0)
              }
            >
              追加
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ProjectAccess;